import { z } from "zod";
import { paginationSchema } from "./common.js";
import { workerCertificationSchema } from "./certifications.js";

export { workerCertificationSchema };

export const workerStatusSchema = z.enum(["ACTIVE", "INACTIVE"]);

export const createWorkerSchema = z.object({
  employeeId: z
    .string()
    .trim()
    .min(1, "Employee ID is required")
    .max(64)
    .regex(/^[A-Za-z0-9-_]+$/, "Employee ID may only contain letters, numbers, hyphens, and underscores"),
  internalName: z.string().trim().min(1, "Internal name is required").max(200),
  displayName: z.string().trim().min(1, "Display name is required").max(200),
  baseHourlyRate: z.coerce.number().positive("Base rate must be positive").max(9999),
  trade: z.string().trim().max(100).optional().nullable(),
  crew: z.string().trim().max(100).optional().nullable(),
  siteId: z.string().uuid().optional().nullable(),
  ruleProfileId: z.string().uuid().optional().nullable(),
  status: workerStatusSchema.default("ACTIVE"),
  notes: z.string().trim().max(5000).optional().nullable(),
});

export const updateWorkerSchema = createWorkerSchema.partial();

export const workerListQuerySchema = paginationSchema.extend({
  siteId: z.string().uuid().optional(),
  crew: z.string().trim().optional(),
  trade: z.string().trim().optional(),
  status: workerStatusSchema.optional(),
  q: z.string().trim().optional(),
});

export const workerImportRowSchema = z.object({
  employeeId: z.string().trim().min(1),
  internalName: z.string().trim().min(1),
  displayName: z.string().trim().min(1),
  baseHourlyRate: z.coerce.number().positive(),
  trade: z.string().trim().optional().nullable(),
  crew: z.string().trim().optional().nullable(),
  siteCode: z.string().trim().optional().nullable(),
  ruleProfileName: z.string().trim().optional().nullable(),
  status: workerStatusSchema.optional().default("ACTIVE"),
  notes: z.string().trim().optional().nullable(),
});

export const workerImportSchema = z.object({
  rows: z.array(workerImportRowSchema).min(1).max(500),
});

export const workerSummarySchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  employeeId: z.string(),
  internalName: z.string().nullable(),
  displayName: z.string(),
  baseHourlyRate: z.number().nullable(),
  trade: z.string().nullable(),
  crew: z.string().nullable(),
  siteId: z.string().uuid().nullable(),
  siteName: z.string().nullable(),
  siteCode: z.string().nullable(),
  ruleProfileId: z.string().uuid().nullable(),
  ruleProfileName: z.string().nullable(),
  status: workerStatusSchema,
  notes: z.string().nullable(),
  currentPeriodHours: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const workerTimeEntrySchema = z.object({
  id: z.string().uuid(),
  date: z.string(),
  hours: z.number(),
  status: z.string(),
  siteName: z.string().nullable(),
  jobCode: z.string().nullable(),
});

export const workerEquipmentUsageSchema = z.object({
  id: z.string().uuid(),
  equipmentName: z.string(),
  usageHours: z.number(),
  date: z.string(),
});

export const workerAuditSchema = z.object({
  id: z.string().uuid(),
  action: z.string(),
  actorName: z.string().nullable(),
  createdAt: z.string(),
  summary: z.string().nullable(),
});

export const workerPayPeriodSummarySchema = z.object({
  payPeriod: z
    .object({
      id: z.string().uuid(),
      label: z.string(),
      status: z.string(),
    })
    .nullable(),
  hours: z.object({
    total: z.number(),
    regular: z.number(),
    overtime: z.number(),
    doubleTime: z.number(),
  }),
  grossPay: z.number().nullable(),
});

export const workerDetailSchema = workerSummarySchema.extend({
  canEdit: z.boolean(),
  canViewPayroll: z.boolean(),
  payPeriodSummary: workerPayPeriodSummarySchema,
  timeEntries: z.array(workerTimeEntrySchema),
  equipmentUsage: z.array(workerEquipmentUsageSchema),
  certifications: z.array(workerCertificationSchema),
  auditActivity: z.array(workerAuditSchema),
});

export const workerFormOptionsSchema = z.object({
  sites: z.array(z.object({ id: z.string().uuid(), name: z.string(), code: z.string() })),
  payRuleProfiles: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
  trades: z.array(z.string()),
  crews: z.array(z.string()),
});

export const workerImportResultSchema = z.object({
  created: z.number().int(),
  skipped: z.number().int(),
  errors: z.array(
    z.object({
      row: z.number().int(),
      employeeId: z.string(),
      message: z.string(),
    }),
  ),
});

export type WorkerStatus = z.infer<typeof workerStatusSchema>;
export type CreateWorkerInput = z.infer<typeof createWorkerSchema>;
export type UpdateWorkerInput = z.infer<typeof updateWorkerSchema>;
export type WorkerSummary = z.infer<typeof workerSummarySchema>;
export type WorkerDetail = z.infer<typeof workerDetailSchema>;
export type WorkerFormOptions = z.infer<typeof workerFormOptionsSchema>;
export type WorkerImportResult = z.infer<typeof workerImportResultSchema>;
